<template>
  <div class="leco-content">
    <div class="page-title">トレーニング予定登録</div>
    <div v-if="error != ''" class="alert alert-danger">{{ error }}</div>
    <transition appear mode="out-in">
      <div v-if="mainLoading" class="loader">loading...</div>
      <div v-else class="container-fluid">
        <loading :active.sync="isPostLoading" class="post-loading"></loading>

        <div>
          <form-wizard
            :title="trainingRegWizard.title"
            :subtitle="trainingRegWizard.subTitle"
            :stepSize="trainingRegWizard.stepSize"
            @on-complete="onComplete"
            next-button-text="次へ"
            back-button-text="前へ"
            finish-button-text="確認"
          >
            <!-- ***************************************************************************** -->
            <tab-content
              title="名称"
              class="form-wizard-plan-create"
              :before-change="onChangeValidateName"
            >
              <p>
                1. トレーニングのカテゴリを選択してください。<br />
                　新規トレーニングの場合はトレーニングマスタで「新規追加する」を選択してください
              </p>
              <div class="row">
                <div class="col-sm-4">
                  <div class="category-select-column-head-label">
                    ① 大分類を選択してください
                  </div>

                  <div
                    class="trainig-category-wrapper"
                    v-bind:class="[
                      categoryInfo.mainCategorySelected == 0
                        ? 'input-focus'
                        : ''
                    ]"
                  >
                    <b-form-group>
                      <b-form-radio-group
                        class="category-select-btn"
                        v-model="categoryInfo.mainCategorySelected"
                        :options="categoryInfo.mainCategoryOptions"
                        value-field="id"
                        text-field="training_category_name"
                        button-variant="outline-secondary"
                        name="radio-btn-stacked"
                        buttons
                        stacked
                        @change="get_subcategories"
                      />
                    </b-form-group>
                  </div>
                </div>
                <div class="col-sm-4">
                  <div class="category-select-column-head-label">
                    ② 中分類を選択してください
                  </div>

                  <div
                    class="trainig-category-wrapper"
                    v-if="categoryInfo.subCategoryOptions.length > 0"
                    v-bind:class="[
                      categoryInfo.mainCategorySelected != 0 &&
                      categoryInfo.subCategorySelected == 0
                        ? 'input-focus'
                        : ''
                    ]"
                  >
                    <b-form-group>
                      <b-form-radio-group
                        class="category-select-btn"
                        v-model="categoryInfo.subCategorySelected"
                        :options="categoryInfo.subCategoryOptions"
                        value-field="id"
                        text-field="training_category_name"
                        button-variant="outline-secondary"
                        name="radio-btn-stacked"
                        buttons
                        stacked
                        @change="get_training_masters"
                      ></b-form-radio-group>
                    </b-form-group>
                  </div>
                  <div class="trainig-category-wrapper no-input" v-else>
                    先に大分類を選択してください
                  </div>
                </div>
                <div class="col-sm-4">
                  <div class="float-left category-select-column-head-label">
                    ③ トレーニングマスタを選択してください
                  </div>

                  <div class="clearfix"></div>

                  <div
                    class="trainig-category-wrapper"
                    v-if="categoryInfo.trainingMasterOptions.length > 0"
                    v-bind:class="[
                      categoryInfo.mainCategorySelected != 0 &&
                      categoryInfo.subCategorySelected != 0 &&
                      categoryInfo.trainingMasterSelected == 0
                        ? 'input-focus'
                        : ''
                    ]"
                  >
                    <b-form-group>
                      <b-form-radio-group
                        class="category-select-btn"
                        v-model="categoryInfo.trainingMasterSelected"
                        :options="categoryInfo.trainingMasterOptions"
                        value-field="id"
                        text-field="training_name"
                        button-variant="outline-secondary"
                        name="radio-btn-stacked"
                        buttons
                        stacked
                        @change="set_training_planning_name"
                      ></b-form-radio-group>
                    </b-form-group>
                    <div v-show="isTrainingMasterLoading">
                      <div class="text-center">now loading...</div>
                      <vue-loading
                        type="spin"
                        color="#333"
                        :size="{ width: '30px', height: '30px' }"
                      ></vue-loading>
                    </div>
                  </div>
                  <div class="trainig-category-wrapper no-input" v-else>
                    先に中分類を選択してください
                  </div>
                </div>
              </div>

              <p class="mt-5">
                2. 開催しようとしているトレーニング名を入力してください。<br />
                　※トレーニングマスタ名称に付け加えて「～～～（XX版）」などの追記も可<br />
                　※新規登録の場合この名称がトレーニングマスタとして登録されます
              </p>
              <input
                class="form-control"
                v-model="training_planning_name"
                placeholder="トレーニング名を入力してください"
                v-bind:class="[
                  categoryInfo.trainingMasterSelected != 0 ? 'input-focus' : ''
                ]"
              />
            </tab-content>

            <!-- ********************************************************************************************** -->
            <tab-content
              title="日時"
              class="form-wizard-plan-create"
              :before-change="onChangeValidateDate"
            >
              <p>トレーニングを開催する日程を選択してください。</p>

              <div class="form-group row">
                <label class="col-sm-2">開催日</label>
                <div class="col-sm-10">
                  <input
                    type="date"
                    class="form-control"
                    data-toggle="datepicker"
                    v-model="date"
                    style="float: left; width: fit-content"
                  />
                </div>
              </div>

              <div class="form-group row">
                <label class="col-sm-2">実施時間</label>
                <span class="form-inline col-sm-10">
                  <div class="form-group">
                    <b-form-select
                      v-model="start_hour"
                      class="form-control time-select"
                    >
                      <option value="01">1</option>
                      <option value="02">2</option>
                      <option value="03">3</option>
                      <option value="04">4</option>
                      <option value="05">5</option>
                      <option value="06">6</option>
                      <option value="07">7</option>
                      <option value="08">8</option>
                      <option value="09">9</option>
                      <option value="10">10</option>
                      <option value="11">11</option>
                      <option value="12">12</option>
                      <option value="13">13</option>
                      <option value="14">14</option>
                      <option value="15">15</option>
                      <option value="16">16</option>
                      <option value="17">17</option>
                      <option value="18">18</option>
                      <option value="19">19</option>
                      <option value="20">20</option>
                      <option value="21">21</option>
                      <option value="22">22</option>
                      <option value="23">23</option>
                      <option value="24">24</option>
                    </b-form-select>
                    <label>：</label>
                    <b-form-select
                      v-model="start_minute"
                      class="form-control time-select"
                    >
                      <option value="00">00</option>
                      <option value="15">15</option>
                      <option value="30">30</option>
                      <option value="45">45</option>
                    </b-form-select>
                    <label class="margin-side">～</label>
                    <b-form-select
                      v-model="end_hour"
                      class="form-control time-select"
                    >
                      <option value="01">1</option>
                      <option value="02">2</option>
                      <option value="03">3</option>
                      <option value="04">4</option>
                      <option value="05">5</option>
                      <option value="06">6</option>
                      <option value="07">7</option>
                      <option value="08">8</option>
                      <option value="09">9</option>
                      <option value="10">10</option>
                      <option value="11">11</option>
                      <option value="12">12</option>
                      <option value="13">13</option>
                      <option value="14">14</option>
                      <option value="15">15</option>
                      <option value="16">16</option>
                      <option value="17">17</option>
                      <option value="18">18</option>
                      <option value="19">19</option>
                      <option value="20">20</option>
                      <option value="21">21</option>
                      <option value="22">22</option>
                      <option value="23">23</option>
                      <option value="24">24</option>
                    </b-form-select>
                    <label>：</label>
                    <b-form-select
                      v-model="end_minute"
                      class="form-control time-select"
                    >
                      <option value="00">00</option>
                      <option value="15">15</option>
                      <option value="30">30</option>
                      <option value="45">45</option>
                    </b-form-select>
                  </div>
                </span>
              </div>
            </tab-content>

            <!-- ********************************************************************************************** -->
            <tab-content
              title="講師"
              class="form-wizard-plan-create"
              :before-change="onChangeValidateCoach"
            >
              <dl v-for="coach in coaches" :key="coach.index">
                <div class="form-group row coach_selection">
                  <div class="col-sm-2">講師{{ coach.index + 1 }}</div>
                  <div class="col-sm-10">
                    <div class="coach_wraper">
                      <div>
                        <label>
                          <b-form-group
                            label="講師分類"
                            class="coach_selection"
                          >
                            <b-form-radio-group
                              style="width: 300px"
                              v-model="coach.internal_coach_flag"
                              :options="internal_coach_options"
                              :name="'internal_coach_type_' + coach.index"
                              button-variant="outline-primary"
                              class="select-group"
                              :required="true"
                            ></b-form-radio-group>
                          </b-form-group>
                        </label>

                        <label>
                          <b-form-group
                            label="講師タイプ"
                            class="coach_selection"
                          >
                            <b-form-radio-group
                              style="width: 300px"
                              v-model="coach.coach_type"
                              :options="coach_type_options"
                              :name="'coach_type_' + coach.index"
                              button-variant="outline-primary"
                              class="select-group coach_selection"
                              :required="true"
                            ></b-form-radio-group>
                          </b-form-group>
                        </label>
                        <label class="float-right">
                          <button
                            style="width: 90px"
                            @click="onDeleteCoach(coach.index)"
                            class="btn btn-outline-danger float-right coach-delete-btn"
                            type="button"
                          >
                            削除
                          </button>
                        </label>
                      </div>
                      <div v-show="coach.internal_coach_flag == 1">
                        <v-select
                          :options="users"
                          label="name"
                          v-model="coach.coach_user_id"
                          :reduce="(users) => users.id"
                        >
                        </v-select>
                      </div>
                      <div v-if="coach.internal_coach_flag == 0">
                        <input
                          class="form-control external-coach-input"
                          v-model="coach.coach_name"
                          id=""
                          placeholder="外部講師名を記入してください"
                          maxlength="30"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </dl>
              <button
                @click="onAddCoach"
                class="btn btn-outline-danger coach-add-btn float-right"
                type="button"
              >
                講師追加
              </button>
              <div class="clearfix"></div>
            </tab-content>

            <!-- ********************************************************************************************** -->
            <tab-content
              title="会場"
              class="form-wizard-plan-create"
              :before-change="onChangeValidatePlace"
            >
              <p>トレーニングを開催する場所やTeams設定を選択してください。</p>

              <div class="form-group row">
                <label class="col-sm-2">TeamsURL</label>
                <span class="col-sm-10">
                  <b-form-group class="coach_selection">
                    <b-form-radio-group
                      style="width: 300px"
                      v-model="is_url_create_enabled"
                      :options="is_url_create_enabled_options"
                      :name="'is_url_create_enabled'"
                      button-variant="outline-primary"
                      class="select-group"
                      :required="true"
                      :disabled="is_urlCreateEnabledActive"
                      v-on:change="onChangeIsWebMeetingUrlCreate"
                    ></b-form-radio-group>
                  </b-form-group>
                </span>
              </div>

              <div class="form-group row">
                <label class="col-sm-2">Microsoftテナント</label>
                <div class="col-sm-10">
                  <b-form-group class="coach_selection">
                    <b-form-radio-group
                      v-model="web_meeting_connection_selected"
                      :options="web_meeting_connection_settings"
                      :name="'web_meeting_connection_selected'"
                      button-variant="outline-primary"
                      class="select-group"
                    ></b-form-radio-group>
                  </b-form-group>
                </div>
              </div>

              <div class="form-group row">
                <label class="col-sm-2">主催者</label>
                <div class="col-sm-10">
                  <v-select
                  :options="users"
                  label="name"
                  v-model="host_user_id" 
                  :reduce="(users) => users.id"
                  :clearable="false"
                  placeholder="選択してください"
                  ></v-select>
                  <small class="form-text text-muted mt-2">
                    ※Microsoft予定やTeamsのミーティングオーナーを指定します。Teamsでは主催者のみがブレイクアウトルームの操作が可能です。
                  </small>
                </div>
              </div>

              <div class="form-group row">
                <label class="col-sm-2">録画</label>
                <span class="col-sm-10">
                  <b-form-group class="coach_selection">
                    <b-form-radio-group
                      style="width: 300px"
                      v-model="is_recording_enabled"
                      :options="is_recording_enabled_options"
                      :name="'is_recording_enabled'"
                      button-variant="outline-primary"
                      class="select-group"
                      :required="true"
                      :disabled="is_recordingEnabledActive"
                    ></b-form-radio-group>
                  </b-form-group>
                  <div
                    v-if="is_external_coach_alert"
                    class="alert alert-secondary"
                    style="margin-top: 10px; margin-bottom: 0px"
                  >
                    外部講師の場合、LECO上でTeamsURLを発行することができません。各自で発行して下さい。
                  </div>
                </span>
              </div>

              <div class="form-group row">
                <label class="col-sm-2">会場</label>
                <span class="col-sm-10">
                  <input class="form-control" v-model="place" maxlength="30" />
                </span>
              </div>
            </tab-content>

            <!-- ********************************************************************************************** -->
            <tab-content
              title="案内文"
              class="form-wizard-plan-create"
              :before-change="onChangeValidateAnnounce"
            >
              <p>
                トレーニング資料格納先やトレーニング案内の文章を入力してください。
              </p>

              <div class="form-group row">
                <label class="col-sm-2">資料格納先URL</label>
                <span class="col-sm-10">
                  <input
                    class="form-control"
                    v-model="trainingMaterialUrl"
                    placeholder="トレーニング資料の格納パスが決まっている場合記載。"
                  />
                  ※共有リンク作成時に期限設定がある場合【無期限】にするようにご注意ください。
                </span>
              </div>

              <div class="form-group row">
                <label class="col-sm-2">対象者</label>
                <span class="col-sm-10">
                  <textarea
                    class="form-control"
                    v-model="target_person"
                    rows="2"
                    placeholder="参加してもらいたい人を書きましょう。
この文面が登録時にSlackでアナウンスされます。"
                  />
                </span>
              </div>

              <div class="form-group row">
                <label class="col-sm-2">案内文</label>
                <span class="col-sm-10">
                  <textarea
                    class="form-control"
                    v-model="informationMsg"
                    rows="6"
                    placeholder="参加者への案内文を書きましょう。
この文面が登録時にSlackでアナウンスされます。"
                  />
                </span>
              </div>
            </tab-content>
          </form-wizard>
        </div>

        <!-- ********************************************************************************************** -->
        <ConfirmationModal
          :confirmation_title="confirmation.title"
          :confirmation_top_msg="confirmation.top_msg"
          :confirmation_warn_list="confirmation.warn_list"
          v-if="isShowConfirmationModal"
          v-on:confirm-cancel="cancelConfirmationModal"
          v-on:confirm-ok="okConfirmationModal"
        >
          <div class="training-regist-confirm-tbl-wrapper">
            <table
              class="table-bordered table-hover training-regist-confirm-tbl"
            >
              <thead>
                <tr>
                  <th
                    class="confirm-modal-header-label confirm-tbl-label-column"
                  >
                    項目
                  </th>
                  <th class="confirm-modal-header-value">設定</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td class="confirm-modal-label confirm-tbl-label-column">
                    分類
                  </td>
                  <td class="confirm-modal-value">
                    <span>{{ categoryInfo.mainCategorySelectedName }}</span>
                    <span>　>　</span>
                    <span>{{ categoryInfo.subCategorySelectedName }}</span>
                  </td>
                </tr>
                <tr>
                  <td class="confirm-modal-label confirm-tbl-label-column">
                    トレーニングマスタ
                  </td>
                  <td class="confirm-modal-value">
                    {{ categoryInfo.trainingMasterSelectedName }}
                  </td>
                </tr>
                <tr>
                  <td class="confirm-modal-label confirm-tbl-label-column">
                    トレーニング予定名称
                  </td>
                  <td class="confirm-modal-value">
                    {{ training_planning_name }}
                  </td>
                </tr>
                <tr>
                  <td class="confirm-modal-label confirm-tbl-label-column">
                    日時
                  </td>
                  <td class="confirm-modal-value">
                    {{
                      date +
                      " " +
                      start_hour +
                      ":" +
                      start_minute +
                      "～" +
                      end_hour +
                      ":" +
                      end_minute
                    }}
                  </td>
                </tr>
                <tr>
                  <td class="confirm-modal-label confirm-tbl-label-column">
                    講師
                  </td>
                  <td class="confirm-modal-value">
                    <div v-for="coach in coaches" :key="coach.index">
                      {{
                        coach.internal_coach_flag
                          ? coach.coach_user_name
                          : coach.coach_name
                      }}
                      <span>（</span>
                      {{ coach.internal_coach_flag ? "社員" : "外部講師" }}
                      <span>　</span>
                      {{ coach.coach_type ? "サブ講師" : "メイン講師" }}
                      <span>）</span>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td class="confirm-modal-label confirm-tbl-label-column">
                    TeamsURL
                  </td>
                  <td class="confirm-modal-value">
                    {{ is_url_create_enabled ? "発行する" : "発行しない" }}
                  </td>
                </tr>
                <tr>
                  <td class="confirm-modal-label confirm-tbl-label-column">
                    Microsoftテナント
                  </td>
                  <td class="confirm-modal-value">
                    {{
                      web_meeting_connection_selected_name.length > 0
                        ? web_meeting_connection_selected_name
                        : "ー"
                    }}
                  </td>
                </tr>
                <tr>
                  <td class="confirm-modal-label confirm-tbl-label-column">
                    主催者
                  </td>
                  <td class="confirm-modal-value">
                    {{
                      host_user_id
                        ? (users.find(function (user) {
                            return user.id === host_user_id;
                          }) || {}).name || "ー"
                        : "ー"
                    }}
                  </td>
                </tr>
                <tr>
                  <td class="confirm-modal-label confirm-tbl-label-column">
                    録画
                  </td>
                  <td class="confirm-modal-value">
                    {{ is_recording_enabled ? "録画する" : "録画しない" }}
                  </td>
                </tr>
                <tr>
                  <td class="confirm-modal-label confirm-tbl-label-column">
                    会場
                  </td>
                  <td class="confirm-modal-value">
                    {{ place }}
                  </td>
                </tr>
                <tr>
                  <td class="confirm-modal-label confirm-tbl-label-column">
                    資料格納URL
                  </td>
                  <td class="confirm-modal-value">
                    {{
                      trainingMaterialUrl.length > 0
                        ? trainingMaterialUrl
                        : "（未入力）"
                    }}
                  </td>
                </tr>
                <tr>
                  <td class="confirm-modal-label">対象者</td>
                  <td class="confirm-modal-value">
                    <textarea
                      class="form-control"
                      v-model="target_person"
                      rows="3"
                      placeholder="（未登録）"
                      :disabled="true"
                    />
                  </td>
                </tr>
                <tr>
                  <td class="confirm-modal-label">案内文</td>
                  <td class="confirm-modal-value">
                    <textarea
                      class="form-control"
                      v-model="informationMsg"
                      rows="6"
                      placeholder="（未登録）"
                      :disabled="true"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </ConfirmationModal>

        <TrainingPlanSharingModal
          :training_plan_id="plan_id"
          v-if="isShowSharingModal"
          v-on:announce-ok="okAnnounceModal"
        >
        </TrainingPlanSharingModal>
      </div>
    </transition>
  </div>
</template>

<script>
import Vue from "vue";
import { mapGetters } from "vuex";
import { API_BASE_URL } from "@/common/config";
import axios from "axios";

// vue-loading-overlay（画面全体のローディング）
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";

// vue-loading-temlate（画面一部のローディング）
import { VueLoading } from "vue-loading-template";

// vue-select
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import ConfirmationModal from "@/components/LECO_ConfirmationModal.vue";
import TrainingPlanSharingModal from "@/components/LECO_TrainingPlanSharingModal.vue";

// vue-form-wizard
import { FormWizard, TabContent } from "vue-form-wizard";
import "vue-form-wizard/dist/vue-form-wizard.min.css";
import { faL, faP } from "@fortawesome/free-solid-svg-icons";

// トレーニング名に使用できない文字のリスト
const FORBIDDEN_TITLE_CHARS = Object.freeze([
  '<', '>', '"', ':', '/', '\\', '|', '?', '*', '#', '%'
]);

// 文字クラス用にエスケープ(正規表現で特殊な意味を持つ文字を無効化)
const _escapeForCharClass = (s) => s.replace(/[-\\^$*+?.()|[\]{}]/g, '\\$&');

// リストから動的に正規表現を生成（リスト更新だけで反映される）
const FORBIDDEN_TITLE_REGEX = new RegExp(
  `[${FORBIDDEN_TITLE_CHARS.map(_escapeForCharClass).join('')}]`,
  'g'
);

// 禁止文字があればエラーメッセージを返す
function validateTrainingTitle(title) {
  const hits = title.match(FORBIDDEN_TITLE_REGEX) || [];
  const unique = [...new Set(hits)]; // 重複を除去
  if (unique.length === 0) return { ok: true, message: '' };

  return {
    ok: false,
    message: `トレーニング名に使用できない文字が含まれています。全角に置き換えるか別の文字に変更してください: ${unique.join(' ')}`
  };
}

export default {
  name: "planning",
  data() {
    return {
      training_planning_name: "",
      training_id: "",
      date: "",
      start_hour: "09",
      start_minute: "00",
      end_hour: "12",
      end_minute: "00",
      host_user_id: "",
      //講師情報のリスト
      coaches: [
        {
          index: 0,
          coach_type: 0,
          internal_coach_flag: 1,
          coach_user_id: "",
          coach_user_name: "",
          coach_name: ""
        }
      ],
      internal_coach_options: [
        { text: "社員", value: 1 },
        { text: "外部講師", value: 0 }
      ],
      coach_type_options: [
        { text: "メイン講師", value: 0 },
        { text: "サブ講師", value: 1 }
      ],
      is_url_create_enabled_options: [
        { text: "発行する", value: true },
        { text: "発行しない", value: false }
      ],
      is_recording_enabled_options: [
        { text: "録画する", value: true },
        { text: "録画しない", value: false }
      ],
      is_url_create_enabled: true,
      is_recording_enabled: true,
      is_external_coach_alert: false,
      place: "Teams",
      trainingMaterialUrl: "",
      target_person: "",
      informationMsg: "",
      input_details: {},
      //リストボックスに活用する社員一覧
      users: [
        {
          id: 0,
          name: ""
        }
      ],
      error: "",
      plan_id: "",
      isUserSelected: false,
      isCoachNameInputed: "",
      isDateInputed: "",
      isPostLoading: false,
      isTrainingMasterLoading: false,
      mainLoading: false,
      logger: this.$store.state.logger,
      web_meeting_connection_settings: [],
      web_meeting_connection_selected: 0,
      web_meeting_connection_selected_name: "",
      is_urlCreateEnabledActive: false,
      is_recordingEnabledActive: false,
      isShowConfirmationModal: false,
      isShowSharingModal: false,
      confirmation: {
        title: "トレーニング予定を登録しても良いですか？",
        top_msg:
          "入力内容を確認し、問題がなければ「OK」をクリックしてください。",
        warn_list: []
      },
      trainingRegWizard: {
        title: "トレーニングを登録します",
        subTitle: "5つのステップでトレーニングの予定を設定します",
        stepSize: "sm"
      },
      categoryInfo: {
        mainCategoryOptions: [],
        mainCategorySelected: 0,
        mainCategorySelectedName: "",
        subCategoryOptions: [],
        subCategorySelected: 0,
        subCategorySelectedName: "",
        trainingMasterOptions: [],
        trainingMasterSelected: 0,
        trainingMasterSelectedName: ""
      }
    };
  },
  components: {
    loading: Loading,
    vSelect,
    ConfirmationModal,
    TrainingPlanSharingModal,
    "form-wizard": FormWizard,
    "tab-content": TabContent,
    "vue-loading": VueLoading
  },
  methods: {
    //ウィザードの「完了」ボタンクリック時に実行される処理
    onComplete: function () {
      this.error = "";

      // 確認ダイアログを表示
      this.openConfirmationModal();

      this.logger.debug("start to post a training planning");
    },

    onChangeValidateName() {
      this.error = "";
      if (this.training_planning_name == "") {
        this.error = "トレーニング名を指定してください。";
        return false;
      }
      const titleCheck = validateTrainingTitle(this.training_planning_name);
      if (!titleCheck.ok) {
        this.error = titleCheck.message;
        return false;
      }
      if (this.categoryInfo.mainCategorySelected == 0) {
        this.error = "大分類を指定してください。";
        return false;
      }

      if (this.categoryInfo.subCategorySelected == 0) {
        this.error = "中分類を指定してください。";
        return false;
      }

      if (this.categoryInfo.trainingMasterSelected == 0) {
        this.error = "トレーニングマスタを指定してください。";
        return false;
      }
      return true;
    },

    onChangeValidateCoach() {
      this.error = "";

      for (var i = 0; i < this.coaches.length; i++) {
        //内部講師の場合
        if (this.coaches[i].internal_coach_flag == 1) {
          if (
            this.coaches[i].coach_user_id == "" ||
            this.coaches[i].coach_user_id == "0"
          ) {
            //講師名が空の講師配列は削除する
            this.coaches.splice(i, 1);
          }
          // 確認ダイアログ用に社員の講師名をテキストで保存
          for (let j = 0; j < this.users.length; j++) {
            if (this.users[j].id == this.coaches[i].coach_user_id) {
              this.coaches[i].coach_user_name = this.users[j].name;
            }
          }
        } else {
          //外部講師の場合
          if (this.coaches[i].coach_name == "") {
            //講師名が空の講師配列は削除する
            this.coaches.splice(i, 1);
          }
        }
      }

      //講師情報の不要な変数をクリア
      for (var k = 0; k < this.coaches.length; k++) {
        if (this.coaches[k].internal_coach_flag == 1) {
          // 社内講師の場合、外部講師名をクリア
          this.coaches[k].coach_name = "";
        } else if (this.coaches[k].internal_coach_flag == 0) {
          // 外部講師の場合、講師IDをクリア
          this.coaches[k].coach_user_id = "";
        }
      }

      //講師欄がひとつもない（＝講師ゼロ）場合は、エラーメッセージを表示
      if (this.coaches.length == 0) {
        this.coaches.push({
          index: 0,
          coach_type: 0,
          internal_coach_flag: 1,
          coach_user_id: "",
          coach_name: ""
        });
        this.error = "講師を一人以上登録してください。";
        return false;
      }

      return true;
    },

    onChangeValidateDate() {
      this.error = "";

      // 必須入力チェック（開催日）
      if (this.date == "") {
        this.error = "開催日を選択してください。";
        return false;
      }

      return true;
    },
    onChangeValidatePlace() {
      this.error = "";

      // 必須入力チェック（Microsoftテナント）
      if (
        this.web_meeting_connection_selected == "0"
      ) {
        this.error = "Microsoftテナントを選択してください。";
        return false;
      }
      // 必須入力チェック（主催者）
      if (!this.host_user_id) {
        this.error = "主催者を選択してください。";
        return false;
      }

      // 確認ダイアログ用に選択肢の名称を保存
      for (let i = 0; i < this.web_meeting_connection_settings.length; i++) {
        if (
          this.web_meeting_connection_settings[i].value ==
          this.web_meeting_connection_selected
        ) {
          this.web_meeting_connection_selected_name =
            this.web_meeting_connection_settings[i].text;
        }
      }

      return true;
    },

    onChangeValidateAnnounce() {
      this.error = "";

      // 必須チェックなし
      return true;
    },

    //
    onChangeIsWebMeetingUrlCreate(value) {
      this.logger.debug("onChangeIsWebMeetingUrlCreate:" + value);
      if (value) {
        this.is_recordingEnabledActive = false;
      } else {
        this.is_recording_enabled = false;
        this.is_recordingEnabledActive = true;
      }
    },

    //登録する講師を増やすため処理を行う
    onAddCoach(event) {
      this.logger.debug(event);
      this.coaches.push({
        index: this.coaches.length,
        coach_type: 0,
        internal_coach_flag: 1,
        coach_user_id: "",
        coach_name: ""
      });
    },

    //登録する講師を減らすため処理を行う
    onDeleteCoach(index) {
      this.logger.debug(index);
      this.coaches.splice(index, 1);
      //配列のIndexを洗い替えする
      for (let id = 0; id < this.coaches.length; id++) {
        this.coaches[id].index = id;
      }
    },

    openConfirmationModal() {
      this.isShowConfirmationModal = true;
    },

    openSharingModal() {
      this.isShowSharingModal = true;
    },

    cancelConfirmationModal: function () {
      this.logger.debug("確認モーダルからCancelクリックイベントが返されました");
      this.isShowConfirmationModal = false;
    },

    okConfirmationModal: function () {
      // ローディングアイコン表示
      this.isPostLoading = true;

      this.logger.debug("確認モーダルからOKクリックイベントが返されました");

      let api_url;
      api_url = API_BASE_URL + "training-plannings";

      this.logger.info("coaches:" + this.coaches);

      // 登録APIを呼び出す
      axios
        .post(
          api_url,
          {
            properties: {
              trainingId: this.categoryInfo.trainingMasterSelected,
              subCategoryId: this.categoryInfo.subCategorySelected,
              trainingPlanningName: this.training_planning_name,
              date: this.date,
              startHour: this.start_hour,
              startMinute: this.start_minute,
              endHour: this.end_hour,
              endMinute: this.end_minute,
              trainerInfo: this.coaches,
              web_meeting_conn_setting_id: this.web_meeting_connection_selected,
              is_url_create_enabled: this.is_url_create_enabled,
              is_recording_enabled: this.is_recording_enabled,
              place: this.place,
              training_material_url: this.trainingMaterialUrl,
              targetPerson: this.target_person,
              informationMsg: this.informationMsg,
              teamsHostUserId: this.host_user_id
            }
          },
          {
            headers: {
              Authorization: this.$store.getters.currentUser.idToken.jwtToken
            }
          }
        )

        .then((result) => {
          this.logger.debug("post api result:\n" + JSON.stringify(result));
          this.isShowConfirmationModal = false;
          if ("statusCode" in result.data) {
            //statusCodeが200なら予実一覧に遷移（今登録したものを確認できる）
            if (result.data.statusCode == 200) {
              this.plan_id = String(result.data.body.planning_id);
              this.logger.debug("end to post a training planning");
              this.isPostLoading = false;
              this.openSharingModal();
            } else if (result.data.statusCode == 500) {
              this.logger.error("API status 500 error");
              this.logger.error(JSON.stringify(result));
              this.$router.push("/system-error500");
            } else if (result.data.statusCode == 400) {
              this.error =
                "入力値に誤りがあります。再度入力してください。解決しない場合は、システム管理者経由でLECOチームにご連絡ください";
            } else {
              this.logger.error("API unexpected status error");
              this.logger.error(JSON.stringify(result));
              this.error =
                "エラーが発生しました。お手数をおかけしますが、システム管理者経由でLECOチームにご連絡ください";
            }
          } else {
            this.logger.error("API unexpected response format error");
            this.logger.error(JSON.stringify(result));
            this.$router.push("/system-error500");
          }
          this.isPostLoading = false;
        })
        .catch((error) => {
          this.logger.error("error in post training plan");
          this.logger.error(JSON.stringify(error));
          this.error =
            "エラーが発生しました。お手数をおかけしますが、システム管理者経由でLECOチームにご連絡ください";
          this.isPostLoading = false;
        });
    },
    okAnnounceModal: function () {
      this.logger.debug(
        "トレーニング内容共有モーダルからOKクリックイベントが返されました"
      );
      this.isShowSharingModal = false;
      this.$router.push("/");
    },
    //メインカテゴリが選択されたとき、その値に対応するサブカテゴリのリストを取得
    get_subcategories() {
      // メインカテゴリに該当するサブカテゴリを取り出す
      this.training_planning_name = "";
      this.categoryInfo.trainingMasterOptions = [];
      this.categoryInfo.trainingMasterSelected = 0;
      this.categoryInfo.subCategorySelected = 0;
      this.categoryInfo.subCategoryOptions =
        this.categoryInfo.mainCategoryOptions.find((category) => {
          return category.id == this.categoryInfo.mainCategorySelected;
        }).subCategories;

      // 選択されたボタンの名称を取得
      for (let i = 0; i < this.categoryInfo.mainCategoryOptions.length; i++) {
        if (
          this.categoryInfo.mainCategoryOptions[i].id ==
          this.categoryInfo.mainCategorySelected
        ) {
          this.categoryInfo.mainCategorySelectedName =
            this.categoryInfo.mainCategoryOptions[i].training_category_name;
        }
      }
    },
    get_training_masters(sub_category_id) {
      this.logger.debug(
        "サブカテゴリが選択されました。 sub_category_id:" + sub_category_id
      );
      this.categoryInfo.trainingMasterOptions = [];
      this.isTrainingMasterLoading = true;
      this.training_planning_name = "";

      // 選択されたボタンの名称を取得
      for (let i = 0; i < this.categoryInfo.subCategoryOptions.length; i++) {
        if (
          this.categoryInfo.subCategoryOptions[i].id ==
          this.categoryInfo.subCategorySelected
        ) {
          this.categoryInfo.subCategorySelectedName =
            this.categoryInfo.subCategoryOptions[i].training_category_name;
        }
      }

      //トレーニング名を取得
      let api_url =
        API_BASE_URL + "trainings/?sub_category_id=" + sub_category_id;

      this.logger.info("Start to get training master by sub_category_id");
      axios
        .get(api_url, {
          headers: {
            Authorization: this.$store.getters.currentUser.idToken.jwtToken
          }
        })
        .then((response) => {
          this.logger.debug("<response>:\n" + JSON.stringify(response));
          if ("statusCode" in response.data) {
            if (response.data.statusCode == 200) {
              this.categoryInfo.trainingMasterOptions.push({
                id: -1,
                training_name: "新規追加する"
              });
              for (
                let i = 0;
                i < response.data.body.training_masters.length;
                i++
              ) {
                this.categoryInfo.trainingMasterOptions.push(
                  response.data.body.training_masters[i]
                );
              }
              this.logger.info("end to get training master by sub_category_id");
            } else {
              this.$router.push("/system-error500");
            }
          } else {
            this.$router.push("/system-error500");
          }
          this.isTrainingMasterLoading = false;
        })
        .catch((err) => {
          this.isTrainingMasterLoading = false;
          this.logger.error("error in getting training name");
          this.logger.error(JSON.stringify(err));
          this.$router.push("/system-error500");
        });

      //社員名一覧を取得
      let api_url2 = API_BASE_URL + "users";
      this.logger.debug("Start to get user names");
      axios
        .get(api_url2, {
          headers: {
            Authorization: this.$store.getters.currentUser.idToken.jwtToken
          }
        })
        .then((response) => {
          if ("statusCode" in response.data) {
            if (response.data.statusCode == 200) {
              this.users = response.data.body;
              this.logger.debug("End to get user names");
            } else {
              this.$router.push("/system-error500");
            }
          } else {
            this.$router.push("/system-error500");
          }
        })
        .catch((err) => {
          this.logger.error("error in getting training name");
          this.logger.error(JSON.stringify(err));
          this.$router.push("/system-error500");
        });
    },
    set_training_planning_name(training_master_id) {
      this.logger.debug(
        "トレーニングマスタが選択されました。 training_master_id:" +
          training_master_id
      );

      // 選択されたボタンの名称を取得
      for (let i = 0; i < this.categoryInfo.trainingMasterOptions.length; i++) {
        if (
          this.categoryInfo.trainingMasterOptions[i].id ==
          this.categoryInfo.trainingMasterSelected
        ) {
          this.categoryInfo.trainingMasterSelectedName =
            this.categoryInfo.trainingMasterOptions[i].training_name;
        }
      }

      let training_name = "";
      for (let i = 0; i < this.categoryInfo.trainingMasterOptions.length; i++) {
        if (
          this.categoryInfo.trainingMasterSelected != -1 &&
          this.categoryInfo.trainingMasterOptions[i].id ==
            this.categoryInfo.trainingMasterSelected
        ) {
          this.logger.debug(
            "トレーニングマスタが特定されました。 training_master_name:" +
              this.categoryInfo.trainingMasterOptions[i].training_name
          );
          training_name =
            this.categoryInfo.trainingMasterOptions[i].training_name;
        }
      }

      // トレーニング予定名欄を更新
      this.training_planning_name = training_name;
    }
  }, //methods

  created: function () {
    // 画面スクロールをトップにする
    this.instantScrollTop();

    this.mainLoading = true;

    //社員名一覧を取得
    let api_url2 = API_BASE_URL + "users";
    this.logger.debug("Start to get user names");
    axios
      .get(api_url2, {
        headers: {
          Authorization: this.$store.getters.currentUser.idToken.jwtToken
        }
      })
      .then((response) => {
        if ("statusCode" in response.data) {
          if (response.data.statusCode == 200) {
            this.users = response.data.body;
            this.logger.debug("End to get user names");
          } else {
            this.$router.push("/system-error500");
          }
        } else {
          this.$router.push("/system-error500");
        }
      })
      .catch((err) => {
        this.logger.error("error in getting training name");
        this.logger.error(JSON.stringify(err));
        this.$router.push("/system-error500");
      });

    //Webミーティング接続設定一覧を取得
    let api_url3 = API_BASE_URL + "zoom/connection/settings/";
    this.logger.debug("Start to get web Meeting connection settings");
    axios
      .get(api_url3, {
        headers: {
          Authorization: this.$store.getters.currentUser.idToken.jwtToken
        }
      })
      .then((response) => {
        if ("statusCode" in response.data) {
          if (response.data.statusCode == 200) {
            this.logger.debug(JSON.stringify(response));
            for (let i = 0; i < response.data.body.web_meeting_settings.length; i++) {
              let setting = {
                text: response.data.body.web_meeting_settings[i].setting_name,
                value: response.data.body.web_meeting_settings[i].id
              };
              this.web_meeting_connection_settings.push(setting);
            }
            // 選択肢が1つしかない場合は自動で選択させる
            if (response.data.body.web_meeting_settings.length == 1) {
              this.web_meeting_connection_selected =
                response.data.body.web_meeting_settings[0].id;
              this.web_meeting_connection_selected_name =
                response.data.body.web_meeting_settings[0].setting_name;
            }
          } else {
            this.logger.error("error not 200 get Web Meeting connection settings");
            this.logger.error(JSON.stringify(response));
            this.$router.push("/system-error500");
          }
        } else {
          this.logger.error("error not 200 get Web Meeting connection settings");
          this.logger.error(JSON.stringify(response));
          this.$router.push("/system-error500");
        }
      })
      .catch((err) => {
        this.logger.error(
          "error not found statusCode Web Meeting connection settings"
        );
        this.logger.error(JSON.stringify(err));
        this.$router.push("/system-error500");
      });

    //メインカテゴリーのリストを取得
    let api_url4 = API_BASE_URL + "training-categories";
    this.logger.info("start to get main categories");
    axios
      .get(api_url4, {
        headers: {
          Authorization: this.$store.getters.currentUser.idToken.jwtToken
        }
      })
      .then((response) => {
        if ("statusCode" in response.data) {
          if (response.data.statusCode == 200) {
            this.logger.debug(response);
            this.logger.info("response length:" + response.data.body.length);
            for (let i = 0; i < response.data.body.length; i++) {
              this.logger.info("aaas");
              this.categoryInfo.mainCategoryOptions = response.data.body;
            }
            this.logger.debug(
              JSON.stringify(this.categoryInfo.mainCategoryOptions)
            );
            this.logger.info("End to get main categories");
          } else {
            this.logger.error(
              "API(get) unexpected return response=" +
                JSON.stringify(response) +
                ", uri=" +
                api_url4
            );
            this.$router.push("/system-error500");
          }
        } else {
          this.logger.error(
            "API(get) unexpected return response=" +
              JSON.stringify(response) +
              ", uri=" +
              api_url4
          );
          this.$router.push("/system-error500");
        }
        this.mainLoading = false;
      })
      .catch((err) => {
        this.mainLoading = false;
        this.logger.error(err.response);
        this.$router.push("/system-error500");
      });
  }, //created
  computed: {
    ...mapGetters(["currentUser"])
  }
}; //export
</script>

<style scoped>
.form-group {
  margin-bottom: 1.3rem;
}

.post-loading {
  z-index: 20000;
}

.margin-side {
  margin-right: 10px;
  margin-left: 10px;
}

.form-wizard-plan-create {
  min-height: 500px;
}

.alert {
  padding: 0.3rem;
}

.input-focus {
  background: #f8f8d7;
}

.no-input {
  background: #e9ecef;
}

.category-select-column-head-label {
  background: #6c757d;
  text-align: center;
  color: white;
  min-height: 45px;
  width: 100%;
  padding: 10px;
}

.coach_wraper {
  padding-top: 20px;
  padding-bottom: 20px;
  border-radius: 5px;
  border: 1px solid #d4d9df;
  padding-left: 15px;
  padding-right: 15px;
}

.external-coach-input {
  height: 34px;
}

.coach-add-btn {
  margin-left: 15px;
  margin-right: 15px;
}

.coach-delete-btn {
  margin-left: 15px;
}

.trainig-category-wrapper {
  padding: 10px;
  border: 1px solid #ced4da;
  min-height: 420px;
  max-height: 420px;
  width: 100%;
  overflow-y: auto;
}

.category-select-btn {
  width: 100%;
}

/* 確認ダイアログ用 */
.confirm-modal-header-label,
.confirm-modal-header-value {
  text-align: center;
  background: #6c757d;
  color: white;
  padding: 5px 10px;
}

.confirm-modal-label,
.confirm-modal-value {
  padding: 5px 10px;
}

.training-regist-confirm-tbl-wrapper {
  overflow-y: auto;
  max-height: 65vh;
}

.training-regist-confirm-tbl {
  width: 100%;
  padding: 5px;
  word-break: break-all;
}

.confirm-tbl-label-column {
  width: 200px;
}

.training-regist-confirm-tbl th {
  position: sticky;
  top: 0;
  background: #6c757d;
  z-index: 1;
}

/* タブレット向け */
@media screen and (min-width: 671px) and (max-width: 1240px) {
  .trainig-category-wrapper {
    min-height: 250px;
    max-height: 250px;
  }

  .float-left category-select-column-head-label {
    font-size: 80%;
  }
}

/* スマホ向け */
@media screen and (max-width: 600px) {
  .registry-btn {
    width: 100% !important;
    margin-top: 10px;
    margin-bottom: 20px;
  }
  .time-select {
    width: 60px;
  }

  .trainig-category-wrapper {
    min-height: 200px;
    max-height: 200px;
  }
}
.registry-btn {
  width: 150px;
}

.coach_selection {
  margin-bottom: 0px;
}

/* loadingアイコン用CSS */
.loader {
  font-size: 10px;
  margin: 50px auto;
  text-indent: -9999em;
  width: 5em;
  height: 5em;
  border-radius: 50%;
  background: #000000;
  background: -moz-linear-gradient(
    left,
    #000000 10%,
    rgba(255, 255, 255, 0) 42%
  );
  background: -webkit-linear-gradient(
    left,
    #000000 10%,
    rgba(255, 255, 255, 0) 42%
  );
  background: -o-linear-gradient(left, #000000 10%, rgba(255, 255, 255, 0) 42%);
  background: -ms-linear-gradient(
    left,
    #000000 10%,
    rgba(255, 255, 255, 0) 42%
  );
  background: linear-gradient(
    to right,
    #000000 10%,
    rgba(255, 255, 255, 0) 42%
  );
  position: relative;
  -webkit-animation: load3 1.4s infinite linear;
  animation: load3 1.4s infinite linear;
  -webkit-transform: translateZ(0);
  -ms-transform: translateZ(0);
  transform: translateZ(0);
}
.loader:before {
  width: 50%;
  height: 50%;
  background: #000000;
  border-radius: 100% 0 0 0;
  position: absolute;
  top: 0;
  left: 0;
  content: "";
}
.loader:after {
  background: #ffffff;
  width: 75%;
  height: 75%;
  border-radius: 50%;
  content: "";
  margin: auto;
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
}

@-webkit-keyframes load3 {
  0% {
    -webkit-transform: rotate(0deg);
    transform: rotate(0deg);
  }
  100% {
    -webkit-transform: rotate(360deg);
    transform: rotate(360deg);
  }
}
@keyframes load3 {
  0% {
    -webkit-transform: rotate(0deg);
    transform: rotate(0deg);
  }
  100% {
    -webkit-transform: rotate(360deg);
    transform: rotate(360deg);
  }
}
</style>
